<template>
  <v-dialog :model-value="isVisible" width="700" persistent>
    <v-form>
      <v-card v-if="spot">
        <v-card-title class="d-flex flex-row pa-4 pb-1 ma-1">
          <h4 class="text-capitalize" data-testid="yard-asset-visit-attach-dialog-title">
            {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.headers.hookAsset') }}
          </h4>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            density="comfortable"
            @click="$emit('close')"></v-btn>
        </v-card-title>
        <v-card-text>
          <template v-if="assetVisit && assetContainer">
            <p class="typography-body-md">
              {{
                t(
                  'yard.dialogs.yardViewAssetvisitAttachDialog.messages.thisWillAttachTheSelectedAssetsToTheTruck'
                )
              }}
            </p>

            <v-row class="mt-4 mb-2">
              <v-col cols="6">
                <svg-asset-container :rotation="270" class="fix-truck-rotation" />

                <p class="mt-4 mb-6 typography-body-md-bold">
                  {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.assetsBeingHooked') }}:
                </p>

                <div class="mt-5">
                  <p class="typography-body-md-bold">
                    {{ assetContainer.code }}
                  </p>

                  <v-chip size="x-small" variant="tonal" class="text-capitalize">
                    <strong>
                      {{ t(`core.yard.assetContainerTypes.${camelCase(assetContainer.type)}`) }}
                    </strong>
                  </v-chip>

                  <p class="mt-2 mb-4">
                    <yard-view-asset-container-status-chip
                      :value="getAssetContainerStatus(assetContainer.assetContainerEvents)"
                      :title="getAssetContainerStatus(assetContainer.assetContainerEvents)" />
                  </p>
                </div>
              </v-col>
              <v-divider vertical />
              <v-col cols="5" class="ml-2">
                <yard-view-truck
                  :rotation="270"
                  :asset-visit="assetVisit"
                  :show-container-count="false"
                  class="fix-truck-rotation" />

                <p class="mt-4 mb-6 typography-body-md-bold">
                  {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.carrierPickingUp') }}:
                </p>
                <h4 class="mt-3 mb-4">
                  <v-icon size="small" class="mr-1">mdi-truck-flatbed</v-icon>
                  {{ assetVisit.company?.name || assetVisit.companyHint }}
                </h4>
                <p class="d-flex flex-column typography-body-sm" v-if="appointment?.refNumber">
                  <strong>
                    {{
                      getTranslatedRefNumberDisplayName(
                        $refNumSettings(selectedWarehouse).displayName
                      )
                    }}
                  </strong>
                  {{ appointment.refNumber }}
                </p>
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <p class="typography-body-md">
              {{
                t(
                  'yard.dialogs.yardViewAssetvisitAttachDialog.messages.noAssetVisitOrAssetContainerFoundForThisSpotPleaseReloadThePage'
                )
              }}
            </p>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="$emit('close')"
            data-testid="yard-asset-visit-attach-dialog-cancel">
            {{ t('global.buttons.nevermind') }}
          </v-btn>
          <v-btn
            class="bg-primary"
            data-testid="yard-asset-visit-attach-dialog-submit"
            :disabled="!assetContainer?.id"
            @click="submit">
            {{ t('global.buttons.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { defineComponent, computed } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { getAssetContainerStatus, getTranslatedRefNumberDisplayName } from '@nova/core';
import { camelCase, first } from 'lodash';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetVisitAttachDialog',
  methods: { getTranslatedRefNumberDisplayName, camelCase },
  emits: ['close'],
  props: {
    spot: {
      type: Object,
      required: true
    },
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const yardStore = useYard();

    const spotAssignment = computed(() => first(props.spot.spotAssignments));
    const assetVisit = computed(() => spotAssignment.value?.assetVisit);
    const assetContainer = computed(() => spotAssignment.value?.assetContainer);
    const appointment = computed(() => assetVisit.value?.appointment);

    const submit = async () => {
      if (!assetVisit.value?.id || !assetContainer.value?.id) {
        console.warn('Asset visit or asset container is not available');
        return;
      }

      const res = await yardStore.attachAssetVisit(assetVisit.value.id, {
        [assetContainer.value.id]: 'attach'
      });

      if (res) {
        yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.HOOK, {
          assetVisit: assetVisit.value,
          metadata: {
            asset_container_id: assetContainer.value.id,
            asset_container_type: assetContainer.value.type,
            appointment_id: assetVisit.value.appointmentId
          }
        });
      }
      emit('close');
    };

    return {
      t,
      submit,
      getAssetContainerStatus,
      assetContainer,
      spotAssignment,
      assetVisit,
      appointment,
      selectedWarehouse: yardStore.selectedWarehouseRef
    };
  }
});
</script>
